/**
 * Utility per formattazione date con timezone.
 * - Display: formatta in una timezone (default Europe/Rome).
 * - Backend: formatta sempre in Europe/Rome per scritture verso API/DB (base dati italiana).
 *
 * Convenzione progetto: le date devono essere sempre con locale Europe/Rome
 * se non è specificata una timezone. Usare ROME_TIMEZONE come default.
 *
 * Design pattern: Strategy per display vs backend.
 */

const ROME_TIMEZONE = 'Europe/Rome';

/** Default timezone per l'app: esportata per uso esplicito (es. formattazione date). */
export const DEFAULT_APP_TIMEZONE = ROME_TIMEZONE;

/**
 * Offset in ms per una timezone a una data (positivo = la timezone è avanti rispetto a UTC).
 * Es. Europe/Rome in inverno = +3600000 (1h).
 * @param {Date} date
 * @param {string} timeZone - IANA (es. 'Europe/Rome')
 * @returns {number}
 */
function getTimezoneOffsetMs(date, timeZone) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' }).formatToParts(date);
        const tzPart = parts.find((p) => p.type === 'timeZoneName');
        if (!tzPart) return 0;
        const v = tzPart.value;
        const match = v.match(/GMT([+-])(\d+)(?::(\d+))?/);
        if (!match) return 0;
        const sign = match[1] === '+' ? 1 : -1;
        const hours = parseInt(match[2], 10);
        const minutes = parseInt(match[3] || '0', 10);
        return sign * (hours * 3600 + minutes * 60) * 1000;
    } catch (e) {
        return 0;
    }
}

/**
 * Interpreta una stringa come data/ora in CET (Europe/Rome) e restituisce l'istanza Date (UTC).
 * Il backend invia sempre date in CET; senza questa conversione le stringhe senza "Z" vengono
 * interpretate come ora locale del browser (errore).
 * @param {string} str - "YYYY-MM-DD HH:mm:ss" o "YYYY-MM-DDTHH:mm:ss" (senza Z)
 * @returns {Date|null}
 */
export function parseDateAsCET(str) {
    if (!str || typeof str !== 'string') return null;
    let s = str.trim();
    if (/^\d{4}-\d{2}-\d{2} \d/.test(s) && !s.includes('T')) {
        s = s.replace(' ', 'T');
    }
    const matchWithTime = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|\s)(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?/);
    const matchDateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matchWithTime) {
        const [, y, mo, d, h, mi, sec] = matchWithTime;
        const year = parseInt(y, 10);
        const month = parseInt(mo, 10) - 1;
        const day = parseInt(d, 10);
        const hour = parseInt(h, 10) || 0;
        const min = parseInt(mi, 10) || 0;
        const second = parseInt(sec || '0', 10) || 0;
        const dateAsIfUTC = new Date(Date.UTC(year, month, day, hour, min, second));
        const offsetMs = getTimezoneOffsetMs(dateAsIfUTC, ROME_TIMEZONE);
        return new Date(dateAsIfUTC.getTime() - offsetMs);
    }
    if (matchDateOnly) {
        const [, y, mo, d] = matchDateOnly;
        const year = parseInt(y, 10);
        const month = parseInt(mo, 10) - 1;
        const day = parseInt(d, 10);
        const dateAsIfUTC = new Date(Date.UTC(year, month, day, 0, 0, 0));
        const offsetMs = getTimezoneOffsetMs(dateAsIfUTC, ROME_TIMEZONE);
        return new Date(dateAsIfUTC.getTime() - offsetMs);
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
}

/**
 * Mappa da nomi "display" (es. restituiti dal backend) a identificatori IANA.
 * Intl accetta solo codici IANA (es. "Europe/Rome"), non "Eastern European Time".
 */
const DISPLAY_NAME_TO_IANA = {
    'eastern european time': 'Europe/Athens',
    'eastern european standard time': 'Europe/Athens',
    'eet': 'Europe/Athens',
    'central european time': 'Europe/Rome',
    'central european standard time': 'Europe/Rome',
    'cet': 'Europe/Rome',
    'western european time': 'Europe/London',
    'gmt': 'Europe/London',
    'utc': 'UTC',
    'america/new york': 'America/New_York',
    'eastern time': 'America/New_York',
    'eastern standard time': 'America/New_York',
    'est': 'America/New_York',
    'pacific time': 'America/Los_Angeles',
    'pacific standard time': 'America/Los_Angeles',
    'pst': 'America/Los_Angeles',
};

/**
 * Normalizza timezone per Intl:
 * - Se è un nome display noto (es. "Eastern European Time") → codice IANA.
 * - Se è già in forma IANA (es. "europe/rome") → "Europe/Rome".
 * Altrimenti fallback Europe/Rome.
 * @param {string} [tz]
 * @returns {string}
 */
export function normalizeTimezone(tz) {
    if (!tz || typeof tz !== 'string') return ROME_TIMEZONE;
    const trimmed = tz.trim();
    if (!trimmed) return ROME_TIMEZONE;
    const byDisplayName = DISPLAY_NAME_TO_IANA[trimmed.toLowerCase()];
    if (byDisplayName) return byDisplayName;
    // Forma IANA tipo "europe/rome" o "Europe/Rome"
    if (trimmed.includes('/')) {
        const normalized = trimmed
            .split('/')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
            .join('/');
        return normalized || ROME_TIMEZONE;
    }
    return ROME_TIMEZONE;
}

/**
 * Formatta con Intl nella timezone data; se fallisce (timezone non supportata), usa Europe/Rome.
 * @param {Date} date
 * @param {string} timezone
 * @param {Intl.DateTimeFormatOptions} options
 * @returns {{ formatted: string; usedTz: string }}
 */
function formatInTimezoneSafe(date, timezone, options) {
    const tz = normalizeTimezone(timezone);
    try {
        const formatter = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: tz });
        const formatted = formatter.format(date);
        return { formatted, usedTz: tz };
    } catch (e) {
        try {
            const formatter = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: ROME_TIMEZONE });
            return { formatted: formatter.format(date), usedTz: ROME_TIMEZONE };
        } catch (e2) {
            return { formatted: date.toLocaleString('en-GB'), usedTz: '' };
        }
    }
}

/**
 * Converte input in Date.
 * Le stringhe dal backend sono sempre in CET (Europe/Rome): vengono interpretate come CET e restituite come istante UTC.
 * Normalizza stringhe tipo MySQL "YYYY-MM-DD HH:mm:ss" e le interpreta come CET (non come UTC né come ora locale browser).
 * @param {string|Date|number} input
 * @returns {Date|null}
 */
function parseToDate(input) {
    if (input == null) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
    if (typeof input === 'string') {
        const str = input.trim();
        if (!str) return null;
        // Sentinel MySQL "non impostato": evita di mostrare date tipo 30/11/1899
        if (/^0000-00-00(\s|$)/.test(str)) return null;
        const hasTime = /\d{4}-\d{2}-\d{2}[T\s]\d/.test(str) || /^\d{4}-\d{2}-\d{2}$/.test(str);
        const hasExplicitOffset = /[Z+-]\d{2}:?\d{2}$/.test(str) || str.endsWith('Z');
        if (hasTime && !hasExplicitOffset) {
            return parseDateAsCET(str);
        }
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : d;
    }
    if (typeof input === 'number') {
        const d = new Date(input);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
}

/**
 * Formatta una data in una timezone per DISPLAY (gg/mm/aaaa).
 * @param {string|Date|number} input
 * @param {string} timezone - IANA (es. 'Europe/Rome')
 * @returns {string}
 */
export function formatDateDisplay(input, timezone = ROME_TIMEZONE) {
    const date = parseToDate(input);
    if (!date) return '';
    const { formatted } = formatInTimezoneSafe(date, timezone, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    return formatted;
}

/**
 * Formatta data e ora in una timezone per DISPLAY (gg/mm/aaaa hh:mm).
 * @param {string|Date|number} input
 * @param {string} timezone
 * @returns {string}
 */
export function formatDateTimeDisplay(input, timezone = ROME_TIMEZONE) {
    const date = parseToDate(input);
    if (!date) return '';
    const { formatted } = formatInTimezoneSafe(date, timezone, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    return formatted.replace(',', '');
}

/**
 * Formatta data, ora e secondi in una timezone per DISPLAY (gg/mm/aaaa hh:mm:ss).
 * @param {string|Date|number} input
 * @param {string} timezone
 * @returns {string}
 */
export function formatDateTimeSecondsDisplay(input, timezone = ROME_TIMEZONE) {
    const date = parseToDate(input);
    if (!date) return '';
    const { formatted } = formatInTimezoneSafe(date, timezone, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    return formatted.replace(',', '');
}

/**
 * Formatta solo l'ora in una timezone per DISPLAY (hh:mm).
 * @param {string|Date|number} input
 * @param {string} timezone
 * @returns {string}
 */
export function formatTimeDisplay(input, timezone = ROME_TIMEZONE) {
    const date = parseToDate(input);
    if (!date) return '';
    const { formatted } = formatInTimezoneSafe(date, timezone, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    return formatted;
}

/**
 * Restituisce YYYY-MM-DD nella timezone indicata (per label/asse grafici in quella timezone).
 * @param {Date} date
 * @param {string} timezone
 * @returns {string}
 */
export function toDateStringInTz(date, timezone = ROME_TIMEZONE) {
    const d = parseToDate(date);
    if (!d) return '';
    const tz = normalizeTimezone(timezone);
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(d);
        const get = (type) => parts.find((p) => p.type === type)?.value?.padStart(2, '0') ?? '';
        return `${get('year')}-${get('month')}-${get('day')}`;
    } catch (e) {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: ROME_TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(d);
        const get = (type) => parts.find((p) => p.type === type)?.value?.padStart(2, '0') ?? '';
        return `${get('year')}-${get('month')}-${get('day')}`;
    }
}

/**
 * Restituisce YYYY-MM-DD HH:mm:ss nella timezone indicata.
 * @param {Date} date
 * @param {string} timezone
 * @returns {string}
 */
export function toDateTimeStringInTz(date, timezone = ROME_TIMEZONE) {
    const d = parseToDate(date);
    if (!d) return '';
    const tz = normalizeTimezone(timezone);
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).formatToParts(d);
        const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
        return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
    } catch (e) {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: ROME_TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).formatToParts(d);
        const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
        return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
    }
}

// ---------- Backend (sempre Europe/Rome) ----------

/**
 * Data in formato backend: YYYY-MM-DD in Europe/Rome.
 * @param {string|Date|number} input
 * @returns {string}
 */
export function toBackendDate(input) {
    return toDateStringInTz(input, ROME_TIMEZONE);
}

/**
 * Data e ora in formato backend: YYYY-MM-DD HH:mm:ss in Europe/Rome.
 * @param {string|Date|number} input
 * @returns {string}
 */
export function toBackendDateTime(input) {
    return toDateTimeStringInTz(input, ROME_TIMEZONE);
}

/**
 * Range giornata in Europe/Rome: inizio 00:00:00 e fine 23:59:59.
 * @param {string|Date|number} dateInput - un giorno (solo data)
 * @returns {{ start: string, end: string }}
 */
export function toBackendDateRangeForDay(dateInput) {
    const d = parseToDate(dateInput);
    if (!d) return { start: '', end: '' };
    const dateStr = toBackendDate(d);
    return {
        start: `${dateStr} 00:00:00`,
        end: `${dateStr} 23:59:59`,
    };
}

/**
 * Restituisce la data/ora "ora" in Europe/Rome per invio al backend (es. "Mark as done").
 * Formato: YYYY-MM-DDTHH:mm:ss (compatibile con backend che si aspetta ora italiana).
 * @param {Date} [date=new Date()]
 * @returns {string}
 */
export function toBackendDateTimeISO(date = new Date()) {
    const d = parseToDate(date) || new Date();
    const dateStr = toBackendDate(d);
    const timeStr = new Intl.DateTimeFormat('en-GB', {
        timeZone: ROME_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).format(d);
    return `${dateStr}T${timeStr}`;
}

export default {
    normalizeTimezone,
    parseDateAsCET,
    formatDateDisplay,
    formatDateTimeDisplay,
    formatDateTimeSecondsDisplay,
    formatTimeDisplay,
    toDateStringInTz,
    toDateTimeStringInTz,
    toBackendDate,
    toBackendDateTime,
    toBackendDateRangeForDay,
    toBackendDateTimeISO,
    parseToDate,
    ROME_TIMEZONE,
    DEFAULT_APP_TIMEZONE,
};
